import React, { useContext, useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { Users } from '../../../../models/User.tsx';
import { editUser, showUser } from '../../../../services/admin/UserService.tsx';
import Switch from 'react-switch';
import { formatNumbers, isFreePlayBet } from '../../../../helpers/functions.tsx';
import { logUserActivity } from '../../../../services/player/LoginService.tsx';
import { useUser } from '../../../../context/UserContext.tsx';
import AgentContext from '../../../../context/admin/Agent.tsx';
import { VisitedUserOutletContext } from '../../../../pages/admin/LayoutUser.tsx';
import { invalidateVisitedUser, visitedUserProfileCacheKey } from '../../../../helpers/adminVisitedUserCache.tsx';
import { subscribeCache } from '../../../../helpers/queryCache.tsx';
import { registerUserBasicsSave } from '../../../../helpers/userBasicsSave.tsx';
import { canAccessAdminCasino } from '../../../../services/player/VimplayService.tsx';
import { IoEyeOffOutline, IoEyeOutline } from 'react-icons/io5';

const emptyDataEdit = {
    pin: '',
    wagerLimit: 0,
    parent: '',
    status: '',
    balanceRolls: 'zeroBalance',
    messaging: false,
    casinoEnabled: false,
    name: '',
    creditLimit: 0,
    available: 0,
    regularBetMaxWager: 0,
    parlayMaxWager: 0,
    propsMaxWager: 0,
    liveMaxWager: 0,
    contestMaxWager: 0,
    teaserMaxWager: 0
};

const buildDataEdit = (result: Users) => ({
    pin: result.pin || '',
    wagerLimit: result.wagerLimit || 0,
    parent: result.parent || '',
    status: result.status,
    balanceRolls: result.balanceRolls || 'zeroBalance',
    messaging: result.messaging || false,
    casinoEnabled: result.casinoEnabled === true,
    name: result.name || '',
    creditLimit: result.creditLimit ?? 0,
    available: result.available ?? 0,
    regularBetMaxWager: result.regularBetMaxWager || 0,
    parlayMaxWager: result.parlayMaxWager || 0,
    propsMaxWager: result.propsMaxWager || 0,
    liveMaxWager: result.liveMaxWager || 0,
    contestMaxWager: result.contestMaxWager || 0,
    teaserMaxWager: result.teaserMaxWager || 0
});

const matchesRouteUser = (u: Users | Partial<Users> | null | undefined, routeUser: string) =>
    !!u?.userName && u.userName.toLowerCase() === routeUser.toLowerCase();

const UserBasics = () => {

    const { user: currentUser, userIpData: userIpDataAdmin, pendingBet } = useUser();
    const { updatePlayerInContext } = useContext(AgentContext);
    const { refetchUser, visitedUser, isVisitedUserLoading } = useOutletContext<VisitedUserOutletContext>();
    const { user } = useParams<{ user: string }>();
    const [formData, setFormData] = useState<Partial<Users> | undefined>(undefined);
    const [dataEdit, setDataEdit] = useState(emptyDataEdit);
    const [creditLimitDraft, setCreditLimitDraft] = useState<string | null>(null);
    const [showPin, setShowPin] = useState(true);
    const [, setProfileCacheTick] = useState(0);
    if (!user) {
        throw new Error("User not authenticated");
    }

    useEffect(() => {
        const key = visitedUserProfileCacheKey(user);
        return subscribeCache<Users>(key, () => {
            setProfileCacheTick(t => t + 1);
        });
    }, [user]);

    const isProfileLoading = isVisitedUserLoading;

    const totalPendingAmount = pendingBet?.reduce((sum: number, bet: any) => {
        if (isFreePlayBet(bet)) return sum;
        return sum + (typeof bet.bet === 'number' ? bet.bet : 0);
    }, 0) || 0;

    const basicSkeletonRows = Array.from({ length: 11 });
    const creditLimitDisplay = creditLimitDraft ?? formatNumbers(dataEdit.creditLimit);

    const applyUserToForm = (result: Users) => {
        setFormData(result);
        setDataEdit(buildDataEdit(result));
        setCreditLimitDraft(null);
    };

    useEffect(() => {
        if (isVisitedUserLoading) return;
        if (visitedUser && matchesRouteUser(visitedUser, user)) {
            applyUserToForm(visitedUser);
        } else if (!visitedUser) {
            setFormData(undefined);
            setDataEdit(emptyDataEdit);
            setCreditLimitDraft(null);
        }
    }, [visitedUser, user, isVisitedUserLoading]);

    const handleInputChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = event.target;

        if (name === "status" || name === "balanceRolls" || name === "pin" || name === "name" || name === "parent") {
            setDataEdit(prev => ({
                ...prev,
                [name]: value,
            }));
            return;
        }

        const cleanValue = value.replace(/,/g, '');

        if (name === "creditLimit") {
            if (cleanValue === "" || cleanValue === "-") {
                setCreditLimitDraft(cleanValue);
                setDataEdit(prev => ({ ...prev, creditLimit: 0 }));
                return;
            }
            if (!/^-?\d*\.?\d*$/.test(cleanValue)) return;
            const parsed = Number(cleanValue);
            if (!Number.isFinite(parsed)) return;
            setCreditLimitDraft(null);
            setDataEdit(prev => ({ ...prev, creditLimit: parsed }));
            return;
        }

        setDataEdit(prev => ({
            ...prev,
            [name]: cleanValue === "" ? 0 : Number(cleanValue),
        }));
    };

    const handleSwitchChange = (checked: boolean) => {
        setDataEdit(prevState => ({
            ...prevState,
            messaging: checked,
        }));
    };

    const handleCasinoChange = (checked: boolean) => {
        setDataEdit(prevState => ({
            ...prevState,
            casinoEnabled: checked,
        }));
    };


    const handleSave = async (playerId: any) => {
        try {
            if (!formData) return;
            if (pendingBet === null) {
                alert('Pending bets are still loading. Please wait and save again.');
                return;
            }

            const freshUser: Users = await showUser(user!);
            const freshBalance = freshUser?.balance ?? 0;
            const used = totalPendingAmount;
            const creditLimit = creditLimitDraft === "-" || creditLimitDraft === ""
                ? 0
                : dataEdit.creditLimit;
            const newAvailable = creditLimit + freshBalance - used;

            const upperPin = dataEdit.pin.toUpperCase();

            const updatedData = {
                pin: upperPin,
                parent: dataEdit.parent,
                status: dataEdit.status,
                balanceRolls: dataEdit.balanceRolls,
                messaging: dataEdit.messaging,
                casinoEnabled: dataEdit.casinoEnabled,
                wagerLimit: dataEdit.wagerLimit,
                name: dataEdit.name,
                creditLimit,
                available: newAvailable,
                pending: used,
                regularBetMaxWager: dataEdit.regularBetMaxWager,
                parlayMaxWager: dataEdit.parlayMaxWager,
                propsMaxWager: dataEdit.propsMaxWager,
                liveMaxWager: dataEdit.liveMaxWager,
                contestMaxWager: dataEdit.contestMaxWager,
                teaserMaxWager: dataEdit.teaserMaxWager
            };

            const prevData = freshUser ?? {};
            const changes: string[] = [];

            (Object.keys(updatedData) as Array<keyof typeof updatedData>).forEach((key) => {
                const prevValue = prevData[key as keyof typeof prevData];
                if (updatedData[key] !== prevValue) {
                    changes.push(`${key}: ${prevValue} → ${updatedData[key]}`);
                }
            });

            await editUser(playerId, updatedData);

            if (typeof updatePlayerInContext === 'function') {
                updatePlayerInContext(playerId, updatedData);
            }

            setFormData(prev => ({
                ...prev,
                ...updatedData
            }));
            setDataEdit(prev => ({
                ...prev,
                pin: upperPin,
                creditLimit,
                available: newAvailable,
            }));
            setCreditLimitDraft(null);

            if (changes.length > 0) {
                logUserActivity(
                    currentUser!.userName,
                    `User Update: ${freshUser?.userName} ${changes.join(", ")}`, userIpDataAdmin
                );
            }

            alert('User updated');
            if (user) invalidateVisitedUser(user);
            refetchUser();
        } catch (error) {
            alert('Error updating user');
        }
    };

    useEffect(() => {
        registerUserBasicsSave({
            save: () => {
                void handleSave(formData?.id);
            },
            disabled: isProfileLoading || !formData?.id,
        });
    });

    useEffect(() => {
        return () => registerUserBasicsSave(null);
    }, []);

    return (
        <>
            <div className="containerr mt-1">
                {isProfileLoading ? (
                    <div className="child-basic user-basics-skeleton" aria-busy="true" aria-label="Loading user profile">
                        {basicSkeletonRows.map((_, index) => (
                            <div key={index} className="container-basic">
                                <span className="rect-placeholder placeholder-basic-label" />
                                <span className="rect-placeholder placeholder-basic-input" />
                                <div className="line-block" />
                            </div>
                        ))}
                    </div>
                ) : (
                <>
                <div className="child-basic">
                    <div className='container-basic'>
                        <span>Name</span>
                        <input id="name" name="name" className="input-basic letters-selected" type="text" value={dataEdit?.name || ''} onChange={handleInputChange} autoComplete="off" />
                        <div className="line-block"></div>
                    </div>
                    <div className='show-agent'>
                        <span>Agent</span>
                        <span className="readonly-box letters-selected">
                            {dataEdit.parent}
                        </span >
                    </div>
                    <div className='container-basic basics-password'>
                        <span>Pin</span>
                        <div className="basics-password-field">
                            <input id="user-basics-pin" name="pin" className="input-basic letters-selected" type={showPin ? "text" : "password"} value={dataEdit.pin} onChange={handleInputChange} autoComplete="off" />
                            <button type="button" className="basics-password-toggle" aria-label={showPin ? "Hide password" : "Show password"} onClick={() => setShowPin(current => !current)}>
                                {showPin ? <IoEyeOutline /> : <IoEyeOffOutline />}
                            </button>
                        </div>
                        <div className="line-block"></div>
                    </div>
                    <div className='container-basic'>
                        <span>Account Status</span>
                        <select id="status" name="status" className='select-box-admin separator letters-selected' autoComplete="off" value={dataEdit.status}
                            onChange={handleInputChange}>
                            <option value="Active">Active</option>
                            <option value="Read-Only">Read-Only</option>
                            {formData?.userType === "Player" && (
                                <option value="Disabled">Disabled</option>
                            )}

                        </select>
                        <div className="line-block2"></div>
                    </div>

                    {canAccessAdminCasino(currentUser) && formData?.userType === "Player" && (
                        <div className='container-menssaging'>
                            <span className='letters-selected'>Active Casino</span>
                            <div className="form-check form-switch">
                                <Switch id='casino' name="casinoEnabled" checked={dataEdit.casinoEnabled} onChange={handleCasinoChange}
                                    offColor="#ff6347"
                                    onColor="#4caf50"
                                    offHandleColor="#ffffff"
                                    onHandleColor="#ffffff" />
                            </div>
                        </div>
                    )}

                    <div className='container-basic'>
                        <span>Zero Balance / Weekly</span>
                        <select
                            id="balanceRolls"
                            name="balanceRolls"
                            className='select-box-admin separator letters-selected'
                            autoComplete="off"
                            value={dataEdit.balanceRolls}
                            onChange={handleInputChange}
                        >
                            <option value="standard">Standard</option>
                            <option value="zeroBalance">Zero Balance</option>
                        </select>
                        <div className="line-block2"></div>
                    </div>

                    {formData?.userType === "Player" && (
                        <div className='container-menssaging hide'>
                            <span className='letters-selected'>Messaging</span>
                            <div className="form-check form-switch">
                                <Switch id='active' name="messaging" checked={dataEdit.messaging} onChange={handleSwitchChange}
                                    offColor="#ff6347"
                                    onColor="#4caf50"
                                    offHandleColor="#ffffff"
                                    onHandleColor="#ffffff" />
                            </div>
                        </div>
                    )}

                    <div className='container-basic show-container'>
                        <span>Credit Limit</span>
                        <input id="creditLimit" name="creditLimit" className="input-basic letters-selected" type="text" inputMode="decimal" value={creditLimitDisplay} onChange={handleInputChange} autoComplete="off" />
                        <div className="line-block"></div>
                    </div>

                    <div className='container-basic show-container'>
                        <span>Wager Limit Regular</span>
                        <input id="regularBetMaxWager" name="regularBetMaxWager" className="input-basic letters-selected" type="text" value={formatNumbers(dataEdit.regularBetMaxWager)} onChange={handleInputChange} autoComplete="off" />
                        <div className="line-block"></div>
                    </div>
                    <div className='container-basic show-container'>
                        <span>Wager Limit Parlay</span>
                        <input id="parlayMaxWager" name="parlayMaxWager" className="input-basic letters-selected" type="text" value={formatNumbers(dataEdit.parlayMaxWager)} onChange={handleInputChange} autoComplete="off" />
                        <div className="line-block"></div>
                    </div>
                    <div className='container-basic show-container'>
                        <span>Wager Limit Props</span>
                        <input id="propsMaxWager" name="propsMaxWager" className="input-basic letters-selected" type="text" value={formatNumbers(dataEdit.propsMaxWager)} onChange={handleInputChange} autoComplete="off" />
                        <div className="line-block"></div>
                    </div>
                    <div className='container-basic show-container'>
                        <span>Wager Limit Live</span>
                        <input id="liveMaxWager" name="liveMaxWager" className="input-basic letters-selected" type="text" value={formatNumbers(dataEdit.liveMaxWager)} onChange={handleInputChange} autoComplete="off" />
                        <div className="line-block"></div>
                    </div>
                    <div className='container-basic show-container'>
                        <span>Wager Limit Future</span>
                        <input id="contestMaxWager" name="contestMaxWager" className="input-basic letters-selected" type="text" value={formatNumbers(dataEdit.contestMaxWager)} onChange={handleInputChange} autoComplete="off" />
                        <div className="line-block"></div>
                    </div>
                    <div className='container-basic show-container'>
                        <span>Wager Limit Teaser</span>
                        <input id="teaserMaxWager" name="teaserMaxWager" className="input-basic letters-selected" type="text" value={formatNumbers(dataEdit.teaserMaxWager)} onChange={handleInputChange} autoComplete="off" />
                        <div className="line-block"></div>
                    </div>
                </div>

                <div className="child-basic hide">
                    <div className='container-basic'>
                        <span className='letters-selected'>WebSite</span>
                        <input id="website" name="website" className="input-basic" type="text" value="jocksportsdev.web.app" disabled autoComplete="url" />
                        <div className="line-block"></div>
                    </div>
                    <div className='container-basic'>
                        <span className='letters-selected'>Credit Limit</span>
                        <input id="creditLimit" name="creditLimit" className="input-basic" type="text" inputMode="decimal" value={creditLimitDisplay} autoComplete="off" onChange={handleInputChange} />
                        <div className="line-block"></div>
                    </div>
                    <div className='container-basic'>
                        <span className='letters-selected'>Wager Limit</span>
                        <input id="regularBetMaxWager" name="regularBetMaxWager" className="input-basic " type="text" value={formatNumbers(dataEdit.regularBetMaxWager)} onChange={handleInputChange} autoComplete="off" />
                        <div className="line-block"></div>
                    </div>
                    <div className='container-basic'>
                        <span className='letters-selected'>Wager Limit Parlay</span>
                        <input id="parlayMaxWager" name="parlayMaxWager" className="input-basic" type="text" value={formatNumbers(dataEdit.parlayMaxWager)} onChange={handleInputChange} autoComplete="off" />
                        <div className="line-block"></div>
                    </div>
                    <div className='container-basic'>
                        <span className='letters-selected'>Wager Limit Props</span>
                        <input id="propsMaxWager" name="propsMaxWager" className="input-basic" type="text" value={formatNumbers(dataEdit.propsMaxWager)} onChange={handleInputChange} autoComplete="off" />
                        <div className="line-block"></div>
                    </div>
                    <div className='container-basic'>
                        <span className='letters-selected'>Wager Limit Live</span>
                        <input id="liveMaxWager" name="liveMaxWager" className="input-basic" type="text" value={formatNumbers(dataEdit.liveMaxWager)} onChange={handleInputChange} autoComplete="off" />
                        <div className="line-block"></div>
                    </div>
                    <div className='container-basic'>
                        <span className='letters-selected'>Wager Limit Future</span>
                        <input id="contestMaxWager" name="contestMaxWager" className="input-basic" type="text" value={formatNumbers(dataEdit.contestMaxWager)} onChange={handleInputChange} autoComplete="off" />
                        <div className="line-block"></div>
                    </div>
                    <div className='container-basic'>
                        <span className='letters-selected'>Wager Limit Teaser</span>
                        <input id="teaserMaxWager" name="teaserMaxWager" className="input-basic" type="text" value={formatNumbers(dataEdit.teaserMaxWager)} onChange={handleInputChange} autoComplete="off" />
                        <div className="line-block"></div>
                    </div>
                </div>

                </>
                )}
            </div>
        </>
    )
}

export default UserBasics;